import React, { useMemo, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';
import { type NativePrintProps, NativeReportShell } from '~/nativeLenses/NativePrintBoard';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import { Alert, Box, Button, Chip, Container, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import type ApiError from '~/types/ApiError';

import LtlpManifestFilters from './LtlpManifestFilters';
import LtlpManifestTable, { type ManifestColumn, type ManifestTableRow } from './LtlpManifestTable';
import {
  BOOKING_FIELDS,
  type BookingField,
  LTLP_MANIFEST_PAGE_ID,
  MEASURES,
  type ManifestFilters,
  type Measures,
  REPORT_VIEWS,
  type ReportView,
  bookingRows,
  defaultFilters,
  filtersError,
  isProblem,
  lensFilters,
  manifestCsv,
  measureText,
  monthLabel,
  parseFilters,
  periodLabel,
  readCard,
  totalsOf,
  vendorMonthRows,
  writeFilters,
} from './model';
import { editFromBridge, ltlpManifestPageView } from './pageAdapter';

const LENS_ID = nativeLensId(LTLP_MANIFEST_PAGE_ID);
const SOURCE = 'lux-analytics-prod.reporting.sales · LTLP (bedbank_marriott)';
const ROW_CAP = 10000;

/** Links only from documented IDs, to the existing purchase pages. */
const BOOKING_LINKS: Partial<Record<BookingField, (value: string) => string>> = {
  order_bk: (value) => `/purchases/${encodeURIComponent(value)}`,
  booking_number: (value) => `/purchases/booking-number/${encodeURIComponent(value)}`,
};

const measureColumns: Array<ManifestColumn> = MEASURES.map((measure) => ({ label: measure.label, numeric: true }));
const measureCells = (measures: Measures) =>
  MEASURES.map((measure) => ({ text: measureText(measures[measure.key], measure.currency) }));

function download(name: string, contents: string) {
  const url = URL.createObjectURL(new Blob([contents], { type: 'text/csv;charset=utf-8;' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

function LtlpManifestContent() {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const location = useLocation();
  const navigate = useNavigate();
  const filters = useMemo(() => parseFilters(new URLSearchParams(location.search)), [location.search]);
  const update = (change: Partial<ManifestFilters>) => {
    navigate({
      pathname: location.pathname,
      search: writeFilters({ ...filters, ...change }, new URLSearchParams(location.search)).toString(),
      hash: location.hash,
    });
  };
  const error = filtersError(filters);
  const request = useMemo(() => lensFilters(filters), [filters]);
  const requestedCardIds = [filters.reportView === 'bookings' ? 'bookings' : 'vendor_summary'];
  const query = useGetReportingLeLensData(
    error ? undefined : LENS_ID,
    request,
    true,
    undefined,
    undefined,
    undefined,
    requestedCardIds,
  );
  const cards = query.data?.cards ?? [];
  // Filter values come back with each load; the last full list stays in place while the next one loads.
  const lastOptions = useRef<Record<string, Array<string>>>({});
  if (query.data?.filterOptions && Object.keys(query.data.filterOptions).length) {
    lastOptions.current = query.data.filterOptions;
  }
  const options = lastOptions.current;
  const ready = !error && query.isSuccess && !query.isFetching && !query.isShowingPreviousData;
  const failure = query.error as ApiError | null;

  const isBookings = filters.reportView === 'bookings';
  const result = readCard(cards, isBookings ? 'bookings' : 'vendor_summary');
  const range = periodLabel(isProblem(result) ? undefined : result.card, `${filters.from} to ${filters.to}`);
  const bookings = !isBookings || isProblem(result) ? [] : bookingRows(result);
  const vendorMonths = isBookings || isProblem(result) ? [] : vendorMonthRows(result);
  const totals = isProblem(result) ? null : totalsOf(isBookings ? bookings : vendorMonths);
  const truncated = !isProblem(result) && result.truncated;

  const columns: Array<ManifestColumn> = isBookings
    ? [...BOOKING_FIELDS.map((field) => ({ label: field.label })), ...measureColumns]
    : [{ label: 'Month of Transaction Date Sydt' }, { label: 'Vendor Name' }, ...measureColumns];
  const tableRows: Array<ManifestTableRow> = isBookings
    ? bookings.map((row) => ({
        key: row.key,
        cells: [
          ...BOOKING_FIELDS.map((field) => {
            const value = row.fields[field.key];
            const link = value ? BOOKING_LINKS[field.key] : undefined;
            return { text: value ?? 'Null', href: link?.(value) };
          }),
          ...measureCells(row.measures),
        ],
      }))
    : vendorMonths.map((row) => ({
        key: row.key,
        cells: [{ text: monthLabel(row.month) }, { text: row.vendor }, ...measureCells(row.measures)],
      }));
  const totalCells = totals
    ? [
        `Total (${tableRows.length.toLocaleString('en-AU')} rows)`,
        ...Array.from({ length: columns.length - MEASURES.length - 1 }, () => ''),
        ...measureCells(totals).map((cell) => cell.text),
      ]
    : undefined;
  const csvRows = isBookings
    ? bookings.map((row) => [
        ...BOOKING_FIELDS.map((field) => row.fields[field.key]),
        ...MEASURES.map((measure) => row.measures[measure.key]),
      ])
    : vendorMonths.map((row) => [row.month, row.vendor, ...MEASURES.map((measure) => row.measures[measure.key])]);

  const pageView = ltlpManifestPageView({
    ready,
    filters,
    options,
    period: range,
    totals,
    section: isProblem(result)
      ? null
      : {
          title: isBookings ? 'Bookings' : 'Vendor Summary',
          rows: isBookings
            ? bookings.map((row) => ({
                label: `${row.fields.vendor_name ?? 'Unknown'} · ${row.fields.booking_number ?? 'Unknown'} · ${row.fields.transaction_date_sydt ?? ''}`,
                values: row.measures,
              }))
            : vendorMonths.map((row) => ({ label: `${row.month} · ${row.vendor}`, values: row.measures })),
          truncated,
        },
  });
  usePageBridge({
    pageId: LTLP_MANIFEST_PAGE_ID,
    view: pageView,
    edit: (changes) => {
      if (!ready) {
        throw new Error('Page data is still loading.');
      }
      update(editFromBridge(changes, pageView, filters));
    },
  });

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 }, minWidth: 0 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={1}
        >
          <Box>
            <Typography component="h1" variant="h4" sx={{ fontSize: { xs: 25, sm: 30 }, color: tokens.accent }}>
              LTLP Manifest
            </Typography>
            <Typography variant="body2" color="text.secondary">
              LTLP room reservations and vendor totals · Sydney transaction dates · AUD
            </Typography>
          </Box>
          <PageChatButton />
        </Stack>
        <Tabs
          value={filters.reportView}
          onChange={(_, reportView: ReportView) => update(defaultFilters(reportView))}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="LTLP Manifest sheets"
        >
          {REPORT_VIEWS.map((view) => (
            <Tab key={view.value} value={view.value} label={view.label} />
          ))}
        </Tabs>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          <Chip size="small" variant="outlined" color="primary" label="Live" />
          <Typography variant="caption" color="text.secondary">
            {SOURCE} · rebuilt daily · {query.isFetching ? 'updating…' : range}
          </Typography>
        </Stack>
        <LtlpManifestFilters filters={filters} options={options} onChange={update} />
        <Alert severity="info">
          Guest names aren&rsquo;t shown; use the booking number to look up a guest in Admin.
        </Alert>
        {error && <Alert severity="error">{error}</Alert>}
        {failure && (
          <Alert
            severity={failure.status === 404 || failure.status === 403 ? 'warning' : 'error'}
            action={
              <Button color="inherit" onClick={() => void query.refetch()}>
                Retry
              </Button>
            }
          >
            {failure.status === 404
              ? 'LTLP Manifest is not registered in Library for this environment yet. A reporting admin registers it under Playground → Lens pages → Register native pages.'
              : failure.status === 403
                ? 'You do not have access to LTLP Manifest. Ask a reporting admin to add you to its Library folder.'
                : failure.message || 'LTLP Manifest could not load.'}
          </Alert>
        )}
        {!error && !failure && query.isPending && <Skeleton variant="rounded" height={400} />}
        {!error && query.data && (
          <>
            {isProblem(result) && (
              <Alert severity={query.isFetching ? 'info' : 'error'}>
                {query.isFetching ? 'Loading…' : result.problem}
              </Alert>
            )}
            {truncated && (
              <Alert severity="warning">
                This sheet reached its {ROW_CAP.toLocaleString('en-AU')}-row limit, so some rows and the totals are
                incomplete. Narrow the dates or filters to see every row.
              </Alert>
            )}
            {!isProblem(result) && (
              <LtlpManifestTable
                title={isBookings ? 'Bookings' : 'Vendor Summary'}
                caption={`${range} · ${tableRows.length.toLocaleString('en-AU')} rows`}
                columns={columns}
                rows={tableRows}
                totals={totalCells}
                onCsv={() =>
                  download(
                    `ltlp-manifest-${filters.reportView}-${filters.from}-${filters.to}.csv`,
                    manifestCsv(
                      isBookings ? 'Bookings' : 'Vendor Summary',
                      range,
                      isBookings
                        ? [...BOOKING_FIELDS.map((field) => field.label), ...MEASURES.map((measure) => measure.label)]
                        : ['Month', 'Vendor Name', ...MEASURES.map((measure) => measure.label)],
                      csvRows,
                    ),
                  )
                }
              />
            )}
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere', pb: 2 }}>
              Source: {SOURCE} · {range}. Sums skip missing values. Null means the source has no value.
            </Typography>
          </>
        )}
      </Stack>
    </Container>
  );
}

export default function LtlpManifestPage({ printTarget }: NativePrintProps) {
  return (
    <NativeReportShell
      pageId="ltlp-manifest"
      title="LTLP Manifest"
      printTarget={printTarget}
    >
      <LtlpManifestContent />
    </NativeReportShell>
  );
}
